import React from "react"
import { connect } from "react-redux"
import PropTypes from "prop-types"
import ImmutablePropTypes from "react-immutable-proptypes"
import styled from "styled-components"
import classNames from "classnames"

// Redux
import { datumCellConnection } from "app/static/frontend/sheets/components/cells/cell-selectors/datum-cell-selectors"

// components
import FA4Icon from "app/static/frontend/components/Icon"
import Tooltip from "app/static/frontend/components/Tooltip"
import SegueLink from "app/static/frontend/middleware/SegueLink"
import { IconWithTooltip } from "frontend/components/IconWithTooltip"

// Cells
import TransactionDatumCell from "app/static/frontend/sheets/components/cells/TransactionDatumCell"

// Helpers and constants
import { generateStaticUrl, cleanUpHtml } from "shared/imports/sharedHelperFunctions"
import { getPartyKey } from "app/static/frontend/sheets/helper-functions"
import sheetConstants from "app/static/frontend/sheets/constants"
import { Avatar, Group } from "@mantine/core"
import { CellWrapper } from "app/static/frontend/sheets/components/cells/MantineCells/CellWrapper"
import { BrandColors } from "shared/imports/sharedConstants"

// Helper component to render tooltip
export const ToolTipCell = ({ text, cell }) => (
    <Tooltip delayShow={sheetConstants.CELL_TOOLTIP_DELAY} label={text}>
        <div>{cell}</div>
    </Tooltip>
)

// Helper component to wrap text and truncate after two lines
const TruncateContents = styled.div`
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    word-break: break-word;
`

// This is the default/fallback DatumCell in case one hasn't been explicitly defined for the given data type.
export const DisplayKeyCell = ({ datum, dataType, ...props }) => (
    <CellWrapper {...props}>
        {cleanUpHtml(datum.get(DjangIO.app.models.QuorumDataType.by_value(dataType).display_key))}
    </CellWrapper>
)

// Render a datum cell for a Person
export const PersonDatumCell = ({ datum, ...props }) => {
    const isCustom = datum.get("organization") ? true : false
    return (
        <ToolTipCell
            text={cleanUpHtml(datum.get("name"))}
            cell={
                // We'll truncate manually, so we can show the Avatar properly
                <CellWrapper truncate={false}>
                    <Group {...props} spacing="xs" align="center" noWrap>
                        <Avatar
                            radius="xl"
                            size="md"
                            alt={`${datum.get("name")} portrait`}
                            className={getPartyKey(datum)}
                            src={datum.get("image_url") ? generateStaticUrl(datum.get("image_url")) : ""}
                        />
                        <TruncateContents>{cleanUpHtml(datum.get("name"))}</TruncateContents>
                        {isCustom && (
                            <IconWithTooltip
                                icon="building"
                                iconColor={BrandColors.BRASS}
                                label="Added by your organization"
                                tooltipPosition="right"
                                withArrow
                            />
                        )}
                    </Group>
                </CellWrapper>
            }
        />
    )
}

export const BillDatumCell = ({ datum, ...props }) => {
    const classes = classNames(
        props.className,
        "long-text",
        `large-text-cell-overflow-hidden large-text-cell-${props.rowHeightFactor || 1}`,
    )
    const regionEnum = DjangIO.app.models.Region.by_value(datum.get("region"))
    const text =
        props.isResourceTable && regionEnum ? `${regionEnum.region_name}: ${datum.get("title")}` : datum.get("title")
    const parsedText = cleanUpHtml(text)
    return (
        <ToolTipCell
            text={parsedText}
            cell={
                <CellWrapper {...props} className={classes}>
                    {parsedText}
                </CellWrapper>
            }
        />
    )
}

export const VoteDatumCell = ({ datum, ...props }) => {
    const text = cleanUpHtml(datum.get("question"))
    return <ToolTipCell text={text} cell={<CellWrapper {...props}>{text}</CellWrapper>} />
}

export const SupporterDatumCell = ({ datum, ...props }) => {
    const text = cleanUpHtml(datum.get("name") || "Unnamed Contact")
    return <ToolTipCell text={text} cell={<CellWrapper {...props}>{text}</CellWrapper>} />
}

export const AmendmentDatumCell = ({ datum, ...props }) => {
    const text = cleanUpHtml(datum.get("title"))
    return <ToolTipCell text={text} cell={<CellWrapper {...props}>{text}</CellWrapper>} />
}

export const PublicOrganizationDatumCell = ({ datum, ...props }) => {
    const text = cleanUpHtml(datum.get("name"))
    return <ToolTipCell text={text} cell={<CellWrapper {...props}>{text}</CellWrapper>} />
}

export const UserDatumCell = ({ datum, ...props }) => {
    const text = datum.get("user_info__full_name") || `${datum.get("first_name")} ${datum.get("last_name")}`
    return <ToolTipCell text={text} cell={<CellWrapper {...props}>{text}</CellWrapper>} />
}

export const NoteDatumCell = ({ datum, ...props }) => (
    <ToolTipCell
        text={cleanUpHtml(datum.get("raw_content"))}
        cell={
            <CellWrapper truncate={false} {...props}>
                <Group {...props} spacing="xs" align="center" noWrap>
                    {datum.getIn(["cache_json", "icon"]) && (
                        <FA4Icon name={datum.getIn(["cache_json", "icon"])} lg={true} className="sheet-row-icon" />
                    )}
                    <TruncateContents>{cleanUpHtml(datum.get("raw_content"))}</TruncateContents>
                </Group>
            </CellWrapper>
        }
    />
)

export const BulkSMSDatumCell = ({ datum, ...props }) => {
    const text = datum.get("description") || "(New Message)"
    return <ToolTipCell text={text} cell={<CellWrapper {...props}>{text}</CellWrapper>} />
}

const datumCellForDataType = (dataType, props) => {
    switch (DjangIO.app.models.QuorumDataType.by_value(dataType).key) {
        case DjangIO.app.models.QuorumDataType.person.key:
        case DjangIO.app.models.QuorumDataType.staffer.key:
        case DjangIO.app.models.QuorumDataType.press_contact.key:
            return <PersonDatumCell {...props} />
        case DjangIO.app.models.QuorumDataType.bill.key:
            return <BillDatumCell {...props} />
        case DjangIO.app.models.QuorumDataType.vote.key:
            return <VoteDatumCell {...props} />
        case DjangIO.app.models.QuorumDataType.supporter.key:
            return <SupporterDatumCell {...props} />
        case DjangIO.app.models.QuorumDataType.public_organization.key:
            return <PublicOrganizationDatumCell {...props} />
        case DjangIO.app.models.QuorumDataType.amendment.key:
            return <AmendmentDatumCell {...props} />
        case DjangIO.app.models.QuorumDataType.user.key:
            return <UserDatumCell {...props} />
        case DjangIO.app.models.QuorumDataType.note.key:
            return <NoteDatumCell {...props} />
        case DjangIO.app.models.QuorumDataType.transaction.key:
            return <TransactionDatumCell {...props} />
        case DjangIO.app.models.QuorumDataType.bulk_sms.key:
            return <BulkSMSDatumCell {...props} />
        default:
            return <DisplayKeyCell dataType={dataType} {...props} />
    }
}

export const DatumCell = ({
    dataType,
    datum,
    optionalPreSegueFunction,
    shouldRenderLink,
    datumCellClassNames,
    cellLink,
    ...props
}) =>
    shouldRenderLink ? (
        <SegueLink
            className={datumCellClassNames}
            to={cellLink}
            optionalPreSegueFunction={optionalPreSegueFunction}
            dataType={dataType}
        >
            {datumCellForDataType(dataType, { className: "datum-cell", datum, ...props })}
        </SegueLink>
    ) : (
        datumCellForDataType(dataType, { className: "datum-cell", datum, ...props })
    )

DatumCell.propTypes = {
    isPublicSheet: PropTypes.bool,
    hasProjectManagementPermission: PropTypes.bool,
    datum: ImmutablePropTypes.map.isRequired,
    dataType: PropTypes.number.isRequired,
    datumUrlOverride: PropTypes.string,
}
DatumCell.defaultProps = {
    isPublicSheet: false,
    hasProjectManagementPermission: false,
    datumUrlOverride: "",
}
DisplayKeyCell.propTypes = {
    dataType: PropTypes.number.isRequired,
    datum: ImmutablePropTypes.map.isRequired,
}
BillDatumCell.propTypes = {
    datum: ImmutablePropTypes.map.isRequired,
}
VoteDatumCell.propTypes = {
    datum: ImmutablePropTypes.map.isRequired,
}
SupporterDatumCell.propTypes = {
    datum: ImmutablePropTypes.map.isRequired,
}
AmendmentDatumCell.propTypes = {
    datum: ImmutablePropTypes.map.isRequired,
}
PublicOrganizationDatumCell.propTypes = {
    datum: ImmutablePropTypes.map.isRequired,
}
PersonDatumCell.propTypes = {
    datum: ImmutablePropTypes.map.isRequired,
}

export default connect((state, props) => datumCellConnection(state, props))(DatumCell) as React.ComponentType<{
    isPublicSheet?: boolean
    hasProjectManagementPermission?: boolean
    datum?: any
    dataType?: any
    datumUrlOverride?: string
    style?: any
    allowDatumLink?: any
    width?: number
}>
